const { LimitedStringSet } = require('utils/limited-string-set.ts');
const invariant = require('invariant');
const MAX_SET_SIZE = 100;
/**
 * ImageQueue Notes:
 * - Does not handle duplicate entries any differently. (LOC to ROI is very poor)
 */

class ImageQueue {
  constructor({maxConnections} = {}) {
    this.reset();
    this.maxConnections = maxConnections || 3;
  }

  reset() {
    this.queue = [];
    this.runners = [];
    this.completedUrls = new LimitedStringSet(MAX_SET_SIZE);
  }

  /*
    source = string identifier, eg: 'hsr'
    imageUrl (required) = image to download
    imgRef = ref to an existing Image to load image to
    callback = (error) => {}
   */
  enqueue({source, imageUrl, imgRef, callback}) {
    invariant(imageUrl, 'You must provide a valid imageUrl');

    this.queue.push({source, imageUrl, imgRef, callback});

    if (this.runners.length < this.maxConnections) {
      this._loadImages();
    }
  }

  dequeue(imageUrl, source = undefined) {
    this.queue = this.queue.filter(item =>
      !((item.imageUrl === imageUrl) && (!source || item.source === source)) // easier to reason about !(dequeueMatch) for filtering
    );
  }

  dequeueAll(source) {
    this.queue = this.queue.filter(item => item.source !== source);
  }

  _loadImages() {
    this.queue
      .splice(0, this.maxConnections - this.runners.length)
      .forEach(task => {
        this._downloadImage(task);
      });
  }

  _downloadImage(task) {
    const image = task.imgRef || new Image(); // eslint-disable-line no-undef

    const handler = (error) => () => {
      const index = this.runners.indexOf(task);
      if (index >= 0) {
        this.runners.splice(index, 1);
      }

      Promise.resolve().then(() => this._loadImages());

      this.completedUrls.add(task.imageUrl);
      if (task.callback) {
        // Call the callback immediately to prevent image-change jitter.
        task.callback(error);
      }
    };

    if (this.completedUrls.has(task.imageUrl)) {
      handler()();

      return;
    }

    image.onerror = handler(new Error(`Error loading image URL: ${task.imageUrl}`));
    image.onload = handler();
    image.src = task.imageUrl;

    task.imgRef = image;
    this.runners.push(task);
  }
}

module.exports = new ImageQueue({maxConnections: 4});
